import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { api } from '../api/robyBackend';
import './Sidebar.css'; // Stellen Sie sicher, dass die CSS-Datei die neuen Klassen enthält
import Cookies from 'js-cookie';


const Sidebar = ({
  sessions,
  setSessions,
  selectSession,
  isVisible,
  toggleSidebar,
  showAppMenu,
  showTemplates,
  setToken,
  currentSession
}) => {
  const currentSessionIdRef = React.useRef(currentSession?._id);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [username, setUsername] = useState('');
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const csrftoken = Cookies.get('csrftoken')
  const navigate = useNavigate();

  // Update currentSessionIdRef wenn currentSession sich ändert
  useEffect(() => {
    currentSessionIdRef.current = currentSession?._id;
  }, [currentSession]);

  // Sessions werden jetzt zentral von ChatApp geladen und verwaltet
  // Setze hasMore basierend auf der Anzahl der aktuellen Sessions
  useEffect(() => {
    // Wenn wir 20 Sessions haben, gibt es wahrscheinlich mehr
    setHasMore(sessions.length >= 20);
  }, [sessions.length]);

  const loadMoreSessions = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const response = await api.get(`/database/sessions?limit=20&skip=${sessions.length}`);
      if (response.data.length > 0) {
        // Kombiniere und sortiere alle Sessions nach updatedAt
        const allSessions = [...sessions, ...response.data].sort((a, b) =>
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        setSessions(allSessions);
        setHasMore(response.data.length === 20);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more sessions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && !isLoadingMore && hasMore) {
      loadMoreSessions();
    }
  };
  
  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await api.get(`/database/user`);
        setUsername(response.data?.firstname || response.data || '');
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    getUser();
  }, [setSessions]);

  // Check the width of the sidebar relative to the screen size
  useEffect(() => {
    const checkSidebarSize = () => {
      if (window.innerWidth < 600) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };
    window.addEventListener('resize', checkSidebarSize);
    // Initial check on component mount
    checkSidebarSize();
    return () => {
      window.removeEventListener('resize', checkSidebarSize);
    };
  }, []);

  const handleLogout = async () => {
    console.log("Logout clicked – sende Anfrage an Django...");

    try {
      const accessToken = localStorage.getItem('access_token');
      
      // 1. Anfrage an Django senden, um dort die Session / Cache / Cookies zu löschen
      const response = await fetch('http://localhost:4000/api-roby/logout/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRFToken': csrftoken,
        },
        credentials: 'include', // Wichtig für Cookies
      });

      if (response.ok) {
        console.log('Logout erfolgreich');
      } else {
        console.warn('Logout auf Server fehlgeschlagen – fahre trotzdem fort');
      }
    } catch (error) {
      console.warn('Logout auf Server fehlgeschlagen – fahre trotzdem fort:', error);
    } finally {
      // 1. LocalStorage komplett leeren
      localStorage.clear();
      
      // 2. Alle Cookies löschen
      const allCookies = Cookies.get();
      Object.keys(allCookies).forEach(cookieName => {
        Cookies.remove(cookieName, { path: '/' });
      });
      
      // 3. WICHTIG: Alle User-States zurücksetzen
      setToken(null);
      //setUser(null); // Falls du einen User-State hast
      // Setze ALLE anderen User-bezogenen States zurück:
      // setUserProfile(null);
      //setPermissions(null);
      // etc.
      
      console.log('Logout abgeschlossen');
      
      // Navigation mit replace: true
      navigate("/login/", { replace: true });
      
      // WICHTIG: Seite neu laden nach Navigation
      window.location.href = '/login/';
    }
  };

  const handleSelectSession = (sessionId) => {
    // Finde den Index der Session mit der übergebenen ID
    const index = sessions.findIndex(s => s._id === sessionId);
    if (index !== -1) {
      selectSession(index); // Session wird ausgewählt
      if (isFullScreen && isVisible) {
        toggleSidebar(); // Sidebar einklappen, wenn auf einem kleinen Bildschirm
      }
    }
  };

  const handleOverviewClick = () => {
    if (!isVisible) {
      toggleSidebar(); // Sidebar ausklappen, wenn sie nicht sichtbar ist
    }
    showAppMenu(); // Zeigt das App-Menü
    if (isFullScreen && isVisible) {
      toggleSidebar(); // Sidebar einklappen, wenn auf einem kleinen Bildschirm
    }
  };

  const handleTemplatesClick = () => {
    if (!isVisible) {
      toggleSidebar();
    }
    showTemplates(); // Zeigt die Templates-Seite
    if (isFullScreen && isVisible) {
      toggleSidebar();
    }
  };

  const handleUserClick = () => {
    window.location.href = '/profile/'; // Ziel URL: robyflow.com/profile/
  };

  // Neu hinzugefügte Funktion zum Handhaben des Klicks auf den Lösch-Button
  const handleDeleteButtonClick = (e, session) => {
    e.stopPropagation(); // Verhindert, dass das Klicken auf den Lösch-Button die Sitzung auswählt
    setSessionToDelete(session);
    setIsDeletePopupVisible(true);
    // Klasse auf body setzen
    document.body.classList.add('delete-popup-open');
  };

  // Funktion zum Bestätigen der Löschung
  const confirmDeleteSession = async () => {
    try {
      // API-Aufruf zum Löschen der Sitzung
      await api.patch(`/database/sessions/${sessionToDelete._id}/delete`);

      // Aktualisiere die Sitzungen im Zustand
      const updatedSessions = sessions.filter((session) => session._id !== sessionToDelete._id);
      setSessions(updatedSessions);

      // Wenn die gelöschte Session die aktuelle ist, zeige AppMenu
      if (currentSession && currentSession._id === sessionToDelete._id) {
        showAppMenu();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    } finally {
      // Schließe das Popup
      setIsDeletePopupVisible(false);
      setSessionToDelete(null);
      // Klasse von body entfernen
      document.body.classList.remove('delete-popup-open');
    }
  };

  // Funktion zum Abbrechen der Löschung
  const cancelDeleteSession = () => {
    setIsDeletePopupVisible(false);
    setSessionToDelete(null);
    // Klasse von body entfernen
    document.body.classList.remove('delete-popup-open');
  };

  return (
    <>
      {/* Menu Button Outside Sidebar When Collapsed */}
      {!isVisible && (
        <div className="menuButtonContainer collapsed">
          <img
            src={process.env.PUBLIC_URL + '/menu.png'}
            alt="Menu"
            className="menuIcon"
            onClick={toggleSidebar}
          />
        </div>
      )}

      {/* Sidebar Content When Expanded */}
      {isVisible && (
        <div
          className={
            isFullScreen ? 'sidebar sidebarFullScreen' : 'sidebar'
          }
        >
          {/* Menu Button Inside Sidebar, Aligned Left */}
          <div className="menuButtonContainer">
            <img
              src={process.env.PUBLIC_URL + '/menu.png'}
              alt="Menu"
              className="menuIcon"
              onClick={toggleSidebar}
            />
          </div>

          {/* Rest of the Sidebar Content */}
          <button className="displayUserButton" onClick={handleUserClick}>
            <h4 className="headingUser">Hi {username}.</h4>
          </button>
          <div className="addButtonContainer">
            <button
              className="newSessionButton"
              onClick={handleOverviewClick}
            >
              <img src={process.env.PUBLIC_URL + '/apps_white.png'} alt="Overview" className="addIcon" />
              Agenten
            </button>
            <button
              className="newSessionButton templatesButton"
              onClick={handleTemplatesClick}
            >
              <img src={process.env.PUBLIC_URL + '/note.png'} alt="Templates" className="addIcon" />
              Templates
            </button>
          </div>
          <h3 className="heading">Chat</h3>
          <ul className="sessionList" onScroll={handleScroll}>
            {sessions
            .filter((session) => !session.deleted) // Filtert Sitzungen, die nicht gelöscht sind
            .map((session) => (
              <li
                key={session._id}
                className={`sessionItem ${currentSession && currentSession._id === session._id ? 'active' : ''}`}
              >
                <div
                  className="sessionItemContent"
                  onClick={() => handleSelectSession(session._id)}
                >
                  <div className="sessionIconContainer">
                    <img
                      src={process.env.PUBLIC_URL + '/chat.png'}
                      alt="Chat"
                      className="sessionIcon"
                    />
                  </div>
                  <div className="sessionTextContainer">
                    <strong className="sessionName">
                      {session.display && session.display !== '...' && !session.display.startsWith('{')
                        ? session.display
                        : (session.display && session.display.startsWith('{') ? 'Bericht' : '...')}
                    </strong>
                    <p className="sessionPreview">
                      {session.display && session.display !== '...' && !session.display.startsWith('{') && !session.display.startsWith('Session')
                        ? (session.display.length > 30 ? session.display.substring(0, 30) + "..." : session.display)
                        : (session.display && session.display.startsWith('{') ? 'Bericht' : (session.name.length > 30 ? session.name.substring(0, 30) + "..." : session.name))}
                    </p>
                  </div>
                </div>
                <button
                  className="deleteSessionButton"
                  onClick={(e) => handleDeleteButtonClick(e, session)}
                >
                  x
                </button>
              </li>
            ))}
            {isLoadingMore && (
              <li className="sessionItem loadingItem">
                <div className="sessionItemContent">
                  <p>Lade weitere Sessions...</p>
                </div>
              </li>
            )}
            {!hasMore && sessions.length > 0 && (
              <li className="sessionItem endItem">
                <div className="sessionItemContent">
                  <p>Alle Sessions geladen</p>
                </div>
              </li>
            )}
          </ul>
          <div className="moreOptionsContainer">
            <a
              href="https://robyflow.com"
              className="moreOptionsText"
              target="_blank"
              rel="noopener noreferrer"
            >
              Powered by robyflow.com
            </a>
{/*             <button
              className="footerButton"
              onClick={() => (window.location.href = 'https://robyflow.com')}
              style={{ marginRight: '6px' }}
            >
              <img
                src={process.env.PUBLIC_URL + '/home.png'}
                alt="Home"
                className="footerButtonIcon"
                title="Zur Startseite von robyflow.com"
              />
            </button> */}
            <button className="footerButton" onClick={handleLogout}>
              <img
                src={process.env.PUBLIC_URL + '/logout.png'}
                alt="Logout"
                className="footerButtonIcon"
                title="Abmelden & Sitzung beenden"
                style={{ marginRight: '6px', marginLeft: '50px' }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Popup zur Bestätigung der Löschung */}
      {isDeletePopupVisible && (
        <div className="deletePopupOverlay" onClick={cancelDeleteSession}>
          <div className="deletePopup" onClick={(e) => e.stopPropagation()}>
            <p>
              Möchten Sie die Sitzung wirklich löschen?
            </p>
            <div className="popupButtons">
              <button
                className="confirmButton"
                onClick={confirmDeleteSession}
              >
                Löschen
              </button>
              <button
                className="cancelButton"
                onClick={cancelDeleteSession}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
